/**
 * @fileoverview A program viewer's portal: which tabs their grants open, and
 * the few words each tab prints the same way.
 *
 * A viewer (a sponsor, a school coordinator) sees one organization's
 * programs, each through the grants the organization gave them. The program
 * page is the org Dashboard's shape cut down to what applies to them:
 *
 *   Overview   the classes, their instructors and the program's details
 *   Lessons    every session, sortable and exportable
 *   Students   the roster, and uploading one
 *   Calendar   the program's meetings, a week at a time
 *   Settings   their emails, the program's contact, leaving the program
 *
 * Overview, Calendar and Settings are every viewer's: the classes and when
 * they meet are what the program is. Lessons opens with a grant that says
 * something about a session (performance, attendance or lesson notes), and
 * Students with the roster or roster upload. What each tab shows inside is
 * still decided per grant on the server, which leaves out what was not
 * granted rather than sending it empty.
 */

import { MONTHS_SHORT, WEEKDAYS, formatTime12h } from "@/lib/schedule";

export const VIEWER_TABS = [
  { id: "overview", label: "Overview", testId: "viewer-tab-overview" },
  { id: "lessons", label: "Lessons", testId: "viewer-tab-lessons" },
  { id: "students", label: "Students", testId: "viewer-tab-students" },
  { id: "calendar", label: "Calendar", testId: "viewer-tab-calendar" },
  { id: "settings", label: "Settings", testId: "viewer-tab-settings" },
];

/** The grants that open the Lessons tab: each says something about a session. */
const LESSON_GRANTS = ["performance", "attendance", "lesson_notes"];

/** The tabs a viewer's grants open, in the bar's order. */
export function viewerTabs(permissions = []) {
  const has = (p) => permissions.includes(p);
  return VIEWER_TABS.filter((t) => {
    if (t.id === "lessons") return LESSON_GRANTS.some(has);
    if (t.id === "students") return has("roster") || has("roster_upload");
    return true;
  });
}

export const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

/** "3:30–4:45 PM" (one meridiem when both ends share it), from a start and a length. */
export function timeRange(timeLocal, minutes) {
  if (!timeLocal) return "";
  const [h, m] = timeLocal.split(":").map(Number);
  const endTotal = h * 60 + m + (minutes || 0);
  const end = `${String(Math.floor(endTotal / 60) % 24).padStart(2, "0")}:${String(endTotal % 60).padStart(2, "0")}`;
  const a = formatTime12h(timeLocal);
  const b = formatTime12h(end);
  if (!minutes) return a;
  return a.slice(-2) === b.slice(-2) ? `${a.slice(0, -3)}–${b}` : `${a}–${b}`;
}

/** "Tuesdays · 3:30–4:45 PM" for a class's meeting pattern (weekdays from Monday, 0). */
export function scheduleLine(schedule) {
  if (!schedule?.weekdays?.length) return "";
  const days = schedule.weekdays.map((d) => `${WEEKDAYS[d]}s`);
  const dayText = days.length > 1 ? `${days.slice(0, -1).join(", ")} and ${days[days.length - 1]}` : days[0];
  return [dayText, timeRange(schedule.time_local, schedule.duration_minutes)].filter(Boolean).join(" · ");
}

/**
 * "6 of 8": present, of the roster on a session's day (Lessons) or of the days a
 * student was marked present or absent (Students). A dash with nothing to count.
 */
export function attendedLabel(att) {
  if (!att || !att.total) return "—";
  return `${att.present} of ${att.total}`;
}

/** Present over the total, for sorting; nothing to count sorts last. */
export const attendedRatio = (att) => (att && att.total ? att.present / att.total : -1);

/** "Oct 6, 2026" from "2026-10-06", from its own parts so it is the same day everywhere. */
export function longDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS_SHORT[m - 1]} ${d}, ${y}`;
}
